/**
 * Video Streaming Upload Saga
 *
 * Main orchestration saga for video upload functionality with:
 * - Automatic retry with exponential backoff
 * - Network error recovery and auto-resume
 * - Progress tracking and monitoring
 * - User cancellation support
 * - Wake lock for mobile devices
 *
 * Architecture:
 * - upload-state.js: Centralized state management
 * - upload-config.js: Configuration and constants
 * - upload-utils.js: Helper functions
 * - upload-xhr.js: XHR handling
 * - upload-monitoring.js: Background monitoring sagas
 *
 * @module streaming-upload
 */

import { put, call, fork, delay, takeLatest } from 'redux-saga/effects'
import generalSaga from '../general'

// Action types
import {
  MEDIA_VIDEO_STREAMING_UPLOAD_START,
  MEDIA_VIDEO_STREAMING_UPLOAD_CANCEL,
} from '../../reducers/media-video/type'

// Actions
import { mediaVideoStreamingUploadErrorAction } from '../../reducers/media-video/action'
import { operationFail } from '../../reducers/operation/action'

// Modules
import uploadState from './upload-state'
import { UPLOAD_CONFIG, LOG_EMOJI } from './upload-config'
import {
  requestWakeLock,
  releaseWakeLock,
  getRetryDelay,
  isRetryableError,
  isNetworkIssue,
  isAbortError,
  getUserFriendlyErrorMessage,
} from './upload-utils'
import { createTusUpload, cancelTusUpload } from './upload-tus'
import {
  networkMonitoringSaga,
  pageVisibilityMonitoringSaga,
  progressMonitoringSaga,
} from './upload-monitoring'

/**
 * Main upload flow with retry logic
 * @param {Object} payload - Upload payload
 * @param {File} payload.file - File to upload
 * @param {string} payload.uploadId - Upload identifier
 * @param {string|null} payload.parentFolderId - Parent folder ID
 */
function* mediaVideoStreamingUploadFlow(payload) {
  const { parentFolderId, file, uploadId } = payload

  // Request wake lock to prevent device from sleeping
  yield call(requestWakeLock, uploadId)

  // Initialize retry counter
  uploadState.setRetryCount(uploadId, 0)

  let lastError = null
  let lastStatusCode = null

  // Retry loop
  for (let attempt = 0; attempt <= UPLOAD_CONFIG.MAX_RETRIES; attempt++) {
    try {
      // If this is a retry, wait for exponential backoff
      if (attempt > 0) {
        const retryDelay = getRetryDelay(attempt - 1)

        // Update progress to show retry status
        uploadState.setProgress(uploadId, {
          message: `Retrying upload (attempt ${attempt + 1}/${
            UPLOAD_CONFIG.MAX_RETRIES + 1
          })...`,
          percentComplete: 0,
          isRetrying: true,
        })

        yield delay(retryDelay)
      }

      // Create progress callback that stores data in state manager
      const progressCallback = progress => {
        uploadState.setProgress(uploadId, progress)
      }

      // Call the TUS upload with progress callback and parentFolderId
      yield call(
        createTusUpload,
        file,
        uploadId,
        progressCallback,
        parentFolderId,
      )

      // Upload successful! Clean up and return
      uploadState.deleteRetryCount(uploadId)
      uploadState.removeFromWaitingQueue(uploadId)

      // Release wake lock
      yield call(releaseWakeLock, uploadId)

      // Don't dispatch success - let progress monitoring handle it
      return
    } catch (error) {
      // Store error details for retry logic
      lastError = error
      lastStatusCode = error.statusCode !== undefined ? error.statusCode : null

      console.error(
        `${LOG_EMOJI.ERROR} Upload attempt ${attempt + 1} failed for ${
          file.name
        }:`,
        error.message,
        `| statusCode: ${lastStatusCode} | navigator.onLine: ${navigator.onLine}`,
      )

      // Check if this upload is being cancelled by user
      const isCancelling = uploadState.isCancelling(uploadId)
      const isAborted = isAbortError(error)

      if (isCancelling || isAborted) {
        // Release wake lock
        yield call(releaseWakeLock, uploadId)
        return // Exit without storing for network restart
      }

      // Check if this is a network-related error
      const isNetwork = isNetworkIssue(error, lastStatusCode)

      if (isNetwork) {
        // Store upload details for restart when online
        uploadState.addToWaitingQueue(uploadId, {
          file,
          uploadId,
          parentFolderId,
          attempt,
        })

        // Update progress with waiting message
        uploadState.setProgress(uploadId, {
          message: 'No internet connection. Upload will resume when connected.',
          percentComplete: 0,
          isWaitingForNetwork: true,
        })

        // Release wake lock while waiting for network
        yield call(releaseWakeLock, uploadId)

        // Exit the retry loop - we'll restart when network comes back
        return
      }

      // Get user-friendly error message
      const userMessage = getUserFriendlyErrorMessage(error, lastStatusCode)

      // Update progress with error message
      uploadState.setProgress(uploadId, {
        message: userMessage,
        percentComplete: 0,
        hasError: true,
        errorType: lastStatusCode ? 'http' : 'network',
      })

      // Check if error is retryable
      const canRetry = isRetryableError(error, lastStatusCode)

      if (!canRetry) {
        console.error(
          `${LOG_EMOJI.ERROR} Non-retryable error for ${file.name}:`,
          error.message,
        )
        break // Exit retry loop for non-retryable errors
      }

      // Check if we've exhausted retries
      if (attempt === UPLOAD_CONFIG.MAX_RETRIES) {
        console.error(
          `${LOG_EMOJI.ERROR} Max retries (${UPLOAD_CONFIG.MAX_RETRIES}) exceeded for ${file.name}`,
        )
        break // Exit retry loop after max retries
      }

      // Update retry counter
      uploadState.setRetryCount(uploadId, attempt + 1)

      // Continue to next retry attempt
    }
  }

  // If we get here, all retries failed
  console.error(
    `${LOG_EMOJI.ERROR} Upload failed after all retries for ${file.name}`,
  )

  // Clean up retry state
  uploadState.deleteRetryCount(uploadId)

  // Release wake lock
  yield call(releaseWakeLock, uploadId)

  // Get final user-friendly error message
  const finalUserMessage = getUserFriendlyErrorMessage(
    lastError,
    lastStatusCode,
  )

  // Dispatch final error with user-friendly message
  yield put(
    mediaVideoStreamingUploadErrorAction(
      uploadId,
      finalUserMessage || 'Upload failed after all retries',
    ),
  )
  yield put(operationFail(lastError || new Error('Upload failed')))
}

/**
 * Handle upload cancellation
 * @param {Object} payload - Cancel payload
 * @param {string} payload.uploadId - Upload identifier
 */
function* mediaVideoStreamingUploadCancelFlow(payload) {
  const { uploadId } = payload

  try {
    // Mark this upload as being cancelled by user
    uploadState.markAsCancelling(uploadId)

    // Cancel the TUS upload request
    yield call(cancelTusUpload, uploadId)

    // Clean up retry state
    const retryCount = uploadState.getRetryCount(uploadId)
    if (retryCount > 0) {
      uploadState.deleteRetryCount(uploadId)
    }

    // Clean up waiting for network state
    if (uploadState.getFromWaitingQueue(uploadId)) {
      uploadState.removeFromWaitingQueue(uploadId)
    }

    // Clean up paused state
    if (uploadState.isPaused(uploadId)) {
      uploadState.unmarkAsPaused(uploadId)
    }

    // Clean up progress tracking
    uploadState.deleteProgress(uploadId)

    // Clean up completion flag
    uploadState.deleteComplete(uploadId)

    // Release wake lock
    yield call(releaseWakeLock, uploadId)

    // Clean up cancelling flag (after all XHR cleanup is done)
    yield delay(100)
    uploadState.unmarkAsCancelling(uploadId)
  } catch (error) {
    // Clean up cancelling flag even if error occurs
    uploadState.unmarkAsCancelling(uploadId)
  }
}

/**
 * Streaming upload watcher
 * Main entry point - forks monitoring sagas and watches for actions
 */
export default function* mediaVideoStreamingUploadWatcher() {
  // Start background monitoring sagas
  yield fork(progressMonitoringSaga)
  yield fork(networkMonitoringSaga)
  yield fork(pageVisibilityMonitoringSaga)

  // Watch for start and cancel actions
  yield takeLatest(MEDIA_VIDEO_STREAMING_UPLOAD_START, function* (action) {
    const { payload } = action

    yield fork(generalSaga, mediaVideoStreamingUploadFlow, payload, false)
  })

  yield takeLatest(MEDIA_VIDEO_STREAMING_UPLOAD_CANCEL, function* (action) {
    const { payload } = action
    yield fork(mediaVideoStreamingUploadCancelFlow, payload)
  })
}
